/**
 * @file frontend/src/components/walkthrough/WalkthroughContext.js
 * @module walkthrough
 * @component WalkthroughProvider
 *
 * Walkthrough Provider — Reusable presentational/UX component.
 *
 * Responsibilities:
 *   - Render the primary UI for this surface.
 *   - Coordinate API calls and local state.
 *   - Emit user actions to parent components / context where applicable.
 *
 * Notes:
 *   - All API calls go through the shared axios client (REACT_APP_BACKEND_URL).
 *   - Side effects belong in `useEffect`; keep render pure.
 */
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import WalkthroughEngine from './WalkthroughEngine';
import { WALKTHROUGH_TOURS } from './walkthroughData';

const WalkthroughContext = createContext({
  startTour: () => {},
  isActive: false,
});

export const useWalkthrough = () => useContext(WalkthroughContext);

export const WalkthroughProvider = ({ children }) => {
  const [activeTourKey, setActiveTourKey] = useState(null);
  const [activeSteps, setActiveSteps] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Remember where the user was when the tour started so we can return them
  // there on completion (otherwise multi-step tours leave them on the last
  // step's page, e.g. /onboarding after the hiring tour).
  const returnPathRef = useRef(null);

  const startTour = useCallback((tourKey, userAudiences = null) => {
    if (!WALKTHROUGH_TOURS[tourKey]) {
      toast.error('Tour not found');
      return;
    }
    returnPathRef.current = location.pathname;

    if (userAudiences && userAudiences.size > 0) {
      const hasAdmin = userAudiences.has('admin');
      const filtered = WALKTHROUGH_TOURS[tourKey].steps.filter(step => {
        if (!step.roles) return true;
        if (hasAdmin) return true;
        return step.roles.some(r => userAudiences.has(r));
      });
      setActiveSteps(filtered.length > 0 ? filtered : WALKTHROUGH_TOURS[tourKey].steps);
    } else {
      setActiveSteps(null);
    }

    setActiveTourKey(tourKey);
  }, [location.pathname]);

  const handleComplete = useCallback(() => {
    setActiveTourKey(null);
    setActiveSteps(null);
    toast.success('Tour completed!');
    if (returnPathRef.current && returnPathRef.current !== location.pathname) {
      navigate(returnPathRef.current);
    }
    returnPathRef.current = null;
  }, [navigate, location.pathname]);

  const handleDismiss = useCallback(() => {
    setActiveTourKey(null);
    setActiveSteps(null);
    if (returnPathRef.current && returnPathRef.current !== location.pathname) {
      navigate(returnPathRef.current);
    }
    returnPathRef.current = null;
  }, [navigate, location.pathname]);

  const activeTour = activeTourKey ? WALKTHROUGH_TOURS[activeTourKey] : null;
  const stepsToRun = activeSteps || (activeTour?.steps ?? []);

  return (
    <WalkthroughContext.Provider value={{ startTour, isActive: !!activeTourKey }}>
      {children}
      {activeTour && (
        <WalkthroughEngine
          key={activeTourKey}
          steps={stepsToRun}
          tourName={activeTour.name}
          onComplete={handleComplete}
          onDismiss={handleDismiss}
          tourKey={activeTourKey}
        />
      )}
    </WalkthroughContext.Provider>
  );
};

export default WalkthroughProvider;
